import { Box, useBreakpointValue, VStack, Text, HStack, Field } from "@chakra-ui/react"
import { Chart, useChart } from "@chakra-ui/charts"
import { Area, AreaChart, XAxis, YAxis } from "recharts"
import { useId } from "react"
import { DateTime, milliseconds, seconds } from "timezonecomplete"


export function formatDateLabel(dateStr: string): string {
    if (!dateStr) return ""
    const parts = dateStr.split("-")
    // Return hour of day
    return parts[2]
}

export interface InlineSparklineProps {
    data: Array<{ name: string; value: number }>
    color?: string
    compact?: boolean
    hideXLabel?: boolean
    start: DateTime
    end: DateTime
    /**
     * Expected spacing between samples (e.g. 5 * 60 * 1000 for 5-minute buckets).
     * When set, gaps longer than ~1.5× this break the line; otherwise the threshold is inferred from the median step (~2.5× median spacing).
     */
    sampleIntervalMs?: number
    unit: string
    field: string
}

const populateData = (data: Array<{ name: string; value: number }>, start: DateTime, end: DateTime, sampleIntervalMs: number): Array<{ name: string; value: number }> => {
    if (!data.length || !sampleIntervalMs) {
        return data
    }

    const buildDataPoint = (timestamp: DateTime, value: number) => {
        return {
            name: timestamp.toIsoString(),
            value: value,
        }
    }

    const result: Array<{ name: string; value: number }> = []
    const lastDataTime = new DateTime(data[data.length - 1].name)

    let currentTime = start
    let dataIndex = 0
    let lastValue = 0

    while (currentTime.valueOf() <= end.valueOf()) {
        const isAfterLastDataPoint = currentTime.valueOf() > lastDataTime.valueOf()

        if (dataIndex < data.length) {
            const nextDataPoint = data[dataIndex]
            const nextDataTime = new DateTime(nextDataPoint.name)

            if (Math.abs(currentTime.valueOf() - nextDataTime.valueOf()) < sampleIntervalMs / 2) {
                result.push(buildDataPoint(currentTime, nextDataPoint.value))
                lastValue = nextDataPoint.value
                dataIndex++
            } else if (currentTime.valueOf() < nextDataTime.valueOf()) {
                result.push(buildDataPoint(currentTime, lastValue))
            } else {
                result.push(buildDataPoint(currentTime, nextDataPoint.value))
                lastValue = nextDataPoint.value
                dataIndex++
                continue
            }
        } else {
            result.push(buildDataPoint(currentTime, isAfterLastDataPoint ? 0 : lastValue))
        }

        currentTime = currentTime.add(milliseconds(sampleIntervalMs))
    }

    return result
}

const convertData = (data: Array<{ name: string; value: number }>, field: string): Array<{ name: string; value: number }> => {
    if (!field || field !== "power") return data
    return data.map((d) => ({ name: d.name, value: d.value !== 0 ? d.value / (field === "power" ? 1000 : 1) : 0 }))
}

export function InlineSparkline({
    data,
    color = "rgba(102, 102, 102, 0.4)",
    compact = false,
    hideXLabel = false,
    start,
    end,
    sampleIntervalMs,
    unit,
    field,
}: InlineSparklineProps) {
    const uniqueId = useId()

    if (!data?.length) data = [{ name: start.toIsoString(), value: 0 }, { name: end.toIsoString(), value: 0 }]

    const gradientId = `gradient-${uniqueId.replace(/:/g, "")}`
    const dataPoints = convertData(populateData(data, start, end, sampleIntervalMs ?? 60 * 5 * 1000), field)


    const values = dataPoints.map((d) => d.value)
    const minValue = Math.min(...values)
    let maxValue = Math.max(...values)
    if (isNaN(maxValue) || maxValue === 0) maxValue = 100
    const range = maxValue - minValue
    const yAxisMin = Math.max(0, minValue - range * 0.3)

    const chart = useChart({
        data: dataPoints,
        series: [{ name: "value", color }],
    })

    const tickFontSize = "6px"
    const chartHeight = "62px"
    const chartMargin = { top: 2, right: 4, bottom: 12, left: 4 }

    return (
        <VStack alignItems="stretch" fontSize="6px" gap={0} px={"2px"} pt={"2px"}>
            <HStack display={!field ? "none" : undefined} >
                <Text textAlign="left" fontWeight={"semibold"}>{field ? field.charAt(0).toUpperCase() + field.slice(1) : ""} ({unit}) since {start.format("HH:mm")}</Text>
            </HStack>
            <HStack>

                <VStack fontSize={tickFontSize} height="100%" py="6px" justifyContent="space-between" width="6px" pl={"2px"}>
                    <Text>{maxValue.toFixed(0)}</Text>
                    <Text>{minValue.toFixed(0)}</Text>
                </VStack>
                <Chart.Root height={chartHeight} chart={chart}>
                    <AreaChart data={chart.data} margin={chartMargin}>
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                                <stop offset="100%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            fontSize={tickFontSize}
                            ticks={data.length > 1 ? [start.toIsoString(), end.toIsoString()] : []}
                            hide
                        />
                        <YAxis hide domain={[yAxisMin, maxValue]} />
                        <Area
                            type="monotone"
                            dataKey={chart.key("value")}
                            stroke={color}
                            strokeWidth={2}
                            fill={`url(#${gradientId})`}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </AreaChart>
                </Chart.Root>
            </HStack>
        </VStack>

    )
}
